/**
 * Where somebody is in setting up their account (Aaron, step 27).
 *
 * "It's going through the stages, but none are indicated on the initial
 * password screen." The password page drew nothing and `/set-up` drew bars
 * numbered from an invite the person never saw — "step 3 of 4" on what was,
 * to them, the second screen. So both pages draw this, from the same named
 * list (`SETUP_STEPS` in shared): one bar per stage with its name under it,
 * done ones ticked, the current one in ink.
 *
 * One stage is not a journey, so a password-only invite draws the company and
 * no bars — a single segment reading "Password" says nothing the heading does
 * not.
 */
import { SETUP_STEP_LABELS, type SetupStep } from '@rowted/shared'
import { IconCheck } from '../frame/icons.js'
import { cn } from '../lib/cn.js'

export function SetupProgress({ company, steps, at }: {
  company: string
  /** Every stage, in the order the flow asks for them. */
  steps: readonly SetupStep[]
  /** The index of the one on screen. */
  at: number
}) {
  const journey = steps.length > 1
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-cell text-text-muted">
        {journey ? `${company} · step ${at + 1} of ${steps.length}` : company}
      </p>
      {journey && (
        <ol className="m-0 flex list-none gap-1.5 p-0" aria-label="Setting up your account">
          {steps.map((step, index) => (
            <li
              key={step}
              aria-current={index === at ? 'step' : undefined}
              className="flex min-w-0 flex-1 flex-col gap-1"
            >
              <span
                aria-hidden
                className={cn('h-1 rounded-tag', index <= at ? 'bg-accent' : 'bg-line')}
              />
              <span
                className={cn('flex items-center gap-1 truncate text-sub',
                  index === at && 'font-semibold text-text',
                  index < at && 'text-accent-dark',
                  index > at && 'text-text-faint')}
              >
                {index < at && <IconCheck size={11} />}
                {SETUP_STEP_LABELS[step]}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
